import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { useTranslation } from 'react-i18next'
import debounce from 'lodash/debounce'
import { useParams } from 'react-router-dom'

import Loading from '../components/shared/Loading/Loading'
import {
  getTransactions,
  getTransactionsForDownload,
  resetTransactionsDownloadState,
  resetTransactionsChartState,
  resetTransactionsState,
  getTransactionsChartByFilter
} from '../store/actions/transactions'
import {
  getMembers,
  getMembersForDownload,
  resetMembersDownloadState,
  resetMembersState,
  changeMemberTransactionState,
  resetMemberTransactionState,
  getMemberBalances,
  getMemberTransactions,
  memberTransaction,
  changeMemberSearchState,
  resetMemberSearchState,
  searchMembers
} from '../store/actions/members'
import {
  getTokens,
  getTokenStatsForLastAndCurrentWeek
} from '../store/actions/token'
import { DASHBOARD_SETUP_STATE } from '../store/reducers/dashboard/dashboardSetup'
import Analytics from '../components/analytics/Analytics'
import { isDashboardReady } from '../util/dashboard.helpers'
import { buildTableHeaders, exportCsv } from '../util/csv.helpers'
import { MEMBERS_TABLE_HEADER } from '../constants/table'
import { formatToShortDate } from '../util/date.helpers'
import { buildMainTransactionsTableHeaders } from '../util/transaction/transaction.helpers'
import { formatTransactionDisplayData } from '../util/transaction/transactionRow.helpers'
import { updateUserAccount } from '../store/actions/account'
import { resetDownloadState } from '../store/actions/download'
import { buildMetadataAttributes } from '../util/account.helpers'
import {
  DEFAULT_CONTAINER_STATE,
  ANALYTICS_PROCESS_STEP,
  QR_CODE_PROCESS_STEP
} from '../constants/containerStates'
import {
  changeAnalyticsFilterState,
  resetAnalyticsFilterState,
  getActiveTotalMembers,
  getExchangeMembers,
  getTopExchangePartners,
  getAnalyticsTokenStats
} from '../store/actions/analytics'
import { DEFAULT_DEBOUNCE_TIME, DOWNLOAD_REPORT_TYPES } from '../constants/app'
import { buildDownloadFileName } from '../util/download.helpers'
import { compareLastWeekWithThisWeek } from '../util/token.helpers'
import { MEMBER_TRANSACTION_PROFILE_DISPATCH } from '../store/reducers/members/memberTransaction'
import { containsNoValues } from '../util/object.helpers'
import {
  checkValidations,
  requiredRule,
  validNaturalNumberRule
} from '../util/validation.helpers'
import { MEMBER_SEARCH_PROFILE_DISPATCH } from '../store/reducers/members/memberSearch'
import { MEMBER_AUTH_ID_MIN_LENGTH } from '../constants/members'
import MemberMoreActionsPopup from '../components/popups/analytics/MemberMoreActions'
import ConfirmedPopup from '../components/popups/common/Confirmed'
import TransactionConfirmationPopup from '../components/popups/analytics/TransactionConfirmation'
import ScanQrCodePopup from '../components/shared/QrCode/ScanQrCode'
import CameraBlockedPopup from '../components/shared/QrCode/CameraBlocked'
import { isAPICompleted, isAPIInProgress } from '../util/app/app.helpers'
import {
  buildAnalyticsUrl,
  fromAnalyticsTabIdToType,
  isAnalyticsPointsBank
} from '../util/analytics/analytics.helpers'
import { formatToCSVText } from '../util/string.helpers'
import { isTokenFullIntegration } from '../util/analytics/pointsBank.helpers'
import { ANALYTICS_FILTER_DISPATCH } from '../store/reducers/analytics/analyticsFilter'
import { ANALYTICS_TAB_TYPE } from '../constants/analytics'

const AnalyticsContainer = ({
  dashboardSetupState,
  getMembers,
  getMembersForDownload,
  resetMembersDownloadState,
  getTransactions,
  getTransactionsForDownload,
  resetTransactionsDownloadState,
  resetTransactionsChartState,
  membersProfile,
  membersDownloadProfile,
  transactionsProfile,
  transactionsDownloadProfile,
  tokenProfile,
  updateUserAccount,
  getTokens,
  getAnalyticsTokenStats,
  downloadProfile,
  resetDownloadState,
  resetTransactionsState,
  resetMembersState,
  brandProfile,
  changeAnalyticsFilterState,
  resetAnalyticsFilterState,
  getTokenStatsForLastAndCurrentWeek,
  mainProfile,
  getTransactionsChartByFilter,
  memberTransactionProfile,
  changeMemberTransactionState,
  resetMemberTransactionState,
  getMemberBalances,
  getMemberTransactions,
  memberTransaction,
  memberSearchProfile,
  changeMemberSearchState,
  resetMemberSearchState,
  searchMembers,
  getActiveTotalMembers,
  getExchangeMembers,
  getTopExchangePartners,
  analyticsFilterProfile,
  history,
  ...restProps
}) => {
  const { t } = useTranslation()
  const { selectedTab } = useParams()

  const setSelectedTab = (selectedTab) => {
    history.push(buildAnalyticsUrl(selectedTab))
  }

  useEffect(() => {
    if (!selectedTab) {
      setSelectedTab(ANALYTICS_TAB_TYPE.POINTS_EXCHANGE)
    } else {
      handleChangeAnalyticsType(selectedTab)
    }
  }, [selectedTab])

  const isFullIntegration = isTokenFullIntegration(tokenProfile.type)
  const isPointsBank = isAnalyticsPointsBank(analyticsFilterProfile.type)

  const [popupState, setPopupState] = useState(DEFAULT_CONTAINER_STATE.NONE)

  const handleChangeTab = (event, selectedTabId) => {
    const newTab = fromAnalyticsTabIdToType(selectedTabId)
    if (selectedTab !== newTab) {
      setSelectedTab(newTab)
    }
  }

  const weeksExchangeValuesDiff = useMemo(() => {
    return compareLastWeekWithThisWeek(mainProfile)
  }, [mainProfile])

  useEffect(() => {
    getTokens()
    return () => {
      resetDownloadState()
    }
  }, [])

  useEffect(() => {
    if (isDashboardReady(dashboardSetupState)) {
      getTokenStatsForLastAndCurrentWeek()
      getActiveTotalMembers()
      getExchangeMembers()
      getTransactionsChartByFilter()
      getTransactions()
      getAnalyticsTokenStats()
      getTopExchangePartners()
      return () => {
        resetTransactionsDownloadState()
        resetMembersDownloadState()
        resetTransactionsChartState()
        resetAnalyticsFilterState()
      }
    } else {
      resetTransactionsDownloadState()
      resetMembersDownloadState()
      resetTransactionsChartState()
      resetTransactionsState()
      resetMembersState()
      //resetAnalyticsFilterState()
      resetMemberTransactionState()
    }
  }, [dashboardSetupState, tokenProfile])

  const handleCloseImportantNote = () => {
    updateUserAccount(buildMetadataAttributes({ hide_analytics_note: true }))
  }

  useEffect(() => {
    const downloadEmailProgress = downloadProfile.downloadEmailProgress
    if (isAPIInProgress(downloadEmailProgress)) {
      setPopupState(ANALYTICS_PROCESS_STEP.SENDING_REPORT_EMAIL)
    }
  }, [downloadProfile.downloadEmailProgress])

  useEffect(() => {
    if (membersDownloadProfile.membersData.length > 0) {
      downloadMembersData(membersDownloadProfile.membersData)
      handleNoPopup()
    }
  }, [membersDownloadProfile.membersData])

  useEffect(() => {
    if (transactionsDownloadProfile.transactionsData.length > 0) {
      downloadTransactionsData(transactionsDownloadProfile.transactionsData)
      handleNoPopup()
    }
  }, [transactionsDownloadProfile.transactionsData])

  useEffect(() => {
    if (
      memberTransactionProfile.transactionAction ===
      ANALYTICS_PROCESS_STEP.MEMBER_TRANSACTION_COMPLETED
    ) {
      setPopupState(ANALYTICS_PROCESS_STEP.MEMBER_MORE_ACTIONS)
    }
  }, [memberTransactionProfile.transactionAction])

  useEffect(() => {
    if (isDashboardReady(dashboardSetupState)) {
      handleDebounceMemberSearch(memberSearchProfile.query)
    }
  }, [dashboardSetupState, memberSearchProfile.query])

  const handleDebounceMemberSearch = useCallback(
    debounce((query) => {
      if (query) {
        searchMembers(query)
      } else {
        getMembers()
      }
    }, DEFAULT_DEBOUNCE_TIME),
    []
  )

  const membersHeaders = buildTableHeaders(t, MEMBERS_TABLE_HEADER)
  const transactionsHeaders = buildMainTransactionsTableHeaders(t, tokenProfile)

  const handleChangeMembersPage = (pageNumber) => {
    getMembers({ pageNumber })
  }

  const handleMembersPerPageChange = (recordsPerPage) => {
    getMembers({ pageNumber: 0, recordsPerPage })
  }

  const handleChangeTransactionsPage = (pageNumber) => {
    getTransactions({ pageNumber })
  }

  const handleTransactionsPerPageChange = (recordsPerPage) => {
    getTransactions({ pageNumber: 0, recordsPerPage })
  }

  const downloadMembersData = (data) => {
    const membersBody = data.map(
      ({ firstName, secondName, email, insertedAt, metadata, authId }) => [
        `${firstName} ${secondName}`,
        email || '',
        authId || '',
        formatToShortDate(insertedAt),
        metadata?.optsNewsletter || ''
      ]
    )
    exportCsv(
      [
        ...membersHeaders.map((header) => header.label),
        t('members.optional-checkbox')
      ],
      membersBody,
      buildDownloadFileName(tokenProfile.name, DOWNLOAD_REPORT_TYPES.MEMBERS)
    )
  }

  const downloadTransactionsData = (data) => {
    const currentBrandId = brandProfile.id
    const transactionsBody = data.map((transaction) => {
      const {
        date,
        exchangeCurrency,
        eventTypeLabel,
        pointAmount,
        exchangeAmount,
        statusLabel,
        rewardTitle,
        loyaltyEventType,
        user,
        txHash,
        additionalData
      } = formatTransactionDisplayData(
        {
          currentTokenId: tokenProfile.id,
          currentBrandId,
          ...transaction
        },
        !isPointsBank
      )
      return [
        `${user?.firstName || ''} ${user?.secondName || ''}`,
        user?.email || '',
        user?.authId || '',
        t(statusLabel) || '',
        date || '',
        txHash || '',
        t(eventTypeLabel) || '',
        pointAmount?.value
          ? `${pointAmount?.valueSymbol || ''}${pointAmount?.value}`
          : '',
        exchangeAmount?.value
          ? `${exchangeAmount?.valueSymbol || ''}${exchangeAmount?.value}`
          : '',
        exchangeCurrency || '',
        loyaltyEventType || '',
        transaction?.code?.code || '',
        formatToCSVText(rewardTitle),
        additionalData?.fees || ''
      ]
    })

    exportCsv(
      [
        ...transactionsHeaders.map((header) => header.label),
        t('members.event-type'),
        t('members.code'),
        t('transactions.reward-title'),
        t('common.fees')
      ],
      transactionsBody,
      buildDownloadFileName(
        tokenProfile.name,
        DOWNLOAD_REPORT_TYPES.TRANSACTIONS
      )
    )
  }

  const handleOnSubmitPopup = () => {
    switch (popupState) {
      case ANALYTICS_PROCESS_STEP.MEMBER_MORE_ACTIONS:
        const { amount } = memberTransactionProfile
        changeMemberTransactionState(
          MEMBER_TRANSACTION_PROFILE_DISPATCH.ERROR,
          {}
        )
        const errors = checkValidations({
          amount: {
            value: amount,
            rules: [requiredRule, validNaturalNumberRule]
          }
        })
        if (containsNoValues(errors)) {
          setPopupState(ANALYTICS_PROCESS_STEP.MEMBER_TRANSACTION_CONFIRMATION)
        } else {
          changeMemberTransactionState(
            MEMBER_TRANSACTION_PROFILE_DISPATCH.ERROR,
            errors
          )
        }
        break

      case ANALYTICS_PROCESS_STEP.MEMBER_TRANSACTION_CONFIRMATION:
        memberTransaction()
        break

      default:
        handleNoPopup()
        break
    }
  }

  const handleOnCancelPopup = () => {
    switch (popupState) {
      case ANALYTICS_PROCESS_STEP.DOWNLOAD_TRANSACTIONS:
      case ANALYTICS_PROCESS_STEP.DOWNLOAD_MEMBERS:
        handleNoPopup()
        resetDownloadState()
        break

      case ANALYTICS_PROCESS_STEP.MEMBER_MORE_ACTIONS:
        resetMemberTransactionState()
        handleNoPopup()
        break

      case ANALYTICS_PROCESS_STEP.MEMBER_TRANSACTION_CONFIRMATION:
        setPopupState(ANALYTICS_PROCESS_STEP.MEMBER_MORE_ACTIONS)
        break

      default:
        handleNoPopup()
    }
  }

  const handleNoPopup = () => {
    setPopupState(DEFAULT_CONTAINER_STATE.NONE)
  }

  const handleMoreMemberActions = (member) => {
    changeMemberTransactionState(
      MEMBER_TRANSACTION_PROFILE_DISPATCH.SELECTED_MEMBER,
      member
    )
    setPopupState(ANALYTICS_PROCESS_STEP.MEMBER_MORE_ACTIONS)

    if (isPointsBank) {
      getMemberBalances()
    } else {
      getMemberTransactions()
    }
  }

  const handleMemberQrCodeClick = () => {
    setPopupState(QR_CODE_PROCESS_STEP.SCAN_CODE)
  }

  const handleMemberResetSearchClick = () => {
    resetMemberSearchState()
  }

  const handleQrCodeError = (error) => {
    console.log(error)
    setPopupState(QR_CODE_PROCESS_STEP.CAMERA_BLOCKED)
  }

  const handleQrCodeScan = (text) => {
    if (text && text.length >= MEMBER_AUTH_ID_MIN_LENGTH) {
      changeMemberSearchState(MEMBER_SEARCH_PROFILE_DISPATCH.QUERY, text)
      handleNoPopup()
    }
  }

  const renderPopup = () => {
    switch (popupState) {
      case ANALYTICS_PROCESS_STEP.MEMBER_MORE_ACTIONS:
        return (
          <MemberMoreActionsPopup
            memberTransactionProfile={memberTransactionProfile}
            onClose={handleOnCancelPopup}
            onSubmit={handleOnSubmitPopup}
            dispatchAction={changeMemberTransactionState}
            t={t}
            membersProfile={membersProfile}
            tokenProfile={tokenProfile}
            brandProfile={brandProfile}
            isPointsBank={isPointsBank}
          />
        )

      case ANALYTICS_PROCESS_STEP.MEMBER_TRANSACTION_CONFIRMATION:
        return (
          <TransactionConfirmationPopup
            t={t}
            onSubmit={handleOnSubmitPopup}
            onClose={handleOnCancelPopup}
            tokenProfile={tokenProfile}
            memberTransactionProfile={memberTransactionProfile}
          />
        )

      case ANALYTICS_PROCESS_STEP.SENDING_REPORT_EMAIL:
        return (
          <ConfirmedPopup
            t={t}
            onSubmit={handleNoPopup}
            title='analytics.csv-file-delivery'
            description='analytics.csv-file-delivery-desc'
            isProcessing={
              !isAPICompleted(downloadProfile.downloadEmailProgress)
            }
          />
        )

      case QR_CODE_PROCESS_STEP.SCAN_CODE:
        return (
          <ScanQrCodePopup
            onClose={handleOnCancelPopup}
            onError={handleQrCodeError}
            onScan={handleQrCodeScan}
            title={t('qr-code.qr-code-search')}
            description={t('qr-code.scan-members-account-number')}
            t={t}
          />
        )
      case QR_CODE_PROCESS_STEP.CAMERA_BLOCKED:
        return <CameraBlockedPopup onClose={handleOnCancelPopup} t={t} />

      default:
        break
    }
  }

  const handleChangeAnalyticsFilter = (property, value) => {
    changeAnalyticsFilterState(property, value)
  }

  const handleChangeAnalyticsType = (selectedTab) => {
    changeAnalyticsFilterState(ANALYTICS_FILTER_DISPATCH.TYPE, selectedTab)
  }

  switch (dashboardSetupState) {
    case DASHBOARD_SETUP_STATE.LOADING:
      return <Loading />
    default:
      return (
        <>
          <Analytics
            onCloseImportantNote={handleCloseImportantNote}
            isDashboardSetupReady={isDashboardReady(dashboardSetupState)}
            membersTableProps={{
              membersHeaders: membersHeaders,
              membersProfile: membersProfile,
              onChangeMembersPage: handleChangeMembersPage,
              onDownloadMembersReport: getMembersForDownload,
              onMembersPerPageChange: handleMembersPerPageChange,
              membersDownloadProfile,
              onMoreMemberActions: handleMoreMemberActions,
              memberSearchProfile,
              changeMemberSearchState,
              onQrCodeClick: handleMemberQrCodeClick,
              onResetSearchClick: handleMemberResetSearchClick
            }}
            transactionsTableProps={{
              transactionsHeaders: transactionsHeaders,
              transactionsProfile: transactionsProfile,
              onChangeTransactionsPage: handleChangeTransactionsPage,
              onDownloadTransactionsReport: getTransactionsForDownload,
              onTransactionsPerPageChange: handleTransactionsPerPageChange,
              transactionsDownloadProfile
            }}
            tokenProfile={tokenProfile}
            brandProfile={brandProfile}
            onChangeAnalyticsFilter={handleChangeAnalyticsFilter}
            weeksExchangeValuesDiff={weeksExchangeValuesDiff}
            onChangeTab={handleChangeTab}
            isFullIntegration={isFullIntegration}
            analyticsFilterProfile={analyticsFilterProfile}
            {...restProps}
            t={t}
            isPointsBank={isPointsBank}
          />
          {renderPopup()}
        </>
      )
  }
}

const mapStateToProps = ({
  transactionsReducer,
  tokensReducer,
  dashboardReducer,
  membersReducer,
  accountReducer,
  mainReducer,
  analyticsReducer
}) => {
  const {
    transactionsGraphProfile,
    transactionsProfile,
    transactionsDownloadProfile,
    exchangeTransactionsGraphProfile
  } = transactionsReducer
  const { brandProfile, dashboardSetupProfile } = dashboardReducer
  const { tokenProfile } = tokensReducer
  const { downloadProfile, billingProfile, mainProfile } = mainReducer
  const { analyticsFilterProfile, pointsBankProfile, pointsExchangeProfile } =
    analyticsReducer
  const {
    membersProfile,
    membersDownloadProfile,
    memberTransactionProfile,
    memberSearchProfile
  } = membersReducer

  return {
    transactionsGraphProfile,
    transactionsProfile,
    transactionsDownloadProfile,
    membersProfile,
    membersDownloadProfile,
    tokenProfile,
    downloadProfile,
    dashboardSetupState: dashboardSetupProfile.dashboardSetupState,
    brandProfile,
    hideAnalyticsNote:
      accountReducer.accountProfile.metadata?.hideAnalyticsNote,
    analyticsFilterProfile,
    mainProfile,
    memberTransactionProfile,
    memberSearchProfile,
    billingProfile,
    pointsBankProfile,
    exchangeTransactionsGraphProfile,
    pointsExchangeProfile
  }
}

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      getAnalyticsTokenStats,
      getMembers,
      getMembersForDownload,
      resetMembersDownloadState,
      getTransactions,
      getTransactionsForDownload,
      resetTransactionsDownloadState,
      resetTransactionsChartState,
      updateUserAccount,
      resetDownloadState,
      getTokens,
      resetTransactionsState,
      resetMembersState,
      changeAnalyticsFilterState,
      resetAnalyticsFilterState,
      getTokenStatsForLastAndCurrentWeek,
      getTransactionsChartByFilter,
      changeMemberTransactionState,
      resetMemberTransactionState,
      getMemberBalances,
      getMemberTransactions,
      memberTransaction,
      changeMemberSearchState,
      resetMemberSearchState,
      searchMembers,
      getActiveTotalMembers,
      getExchangeMembers,
      getTopExchangePartners
    },
    dispatch
  )

export default connect(mapStateToProps, mapDispatchToProps)(AnalyticsContainer)
